import { Controller } from '@hotwired/stimulus';

/**
 * MoeFee line builder — handles "Ajouter une phase" entirely client-side so
 * nothing is persisted to the database until the architect clicks
 * "Enregistrer". Mirrors the existing `_destroy=1` pattern used by the trash
 * button: all mutations are pending until the parent form submits.
 *
 * On click, clones the inner HTML of `templateTarget` (a `<template>` carrying
 * an empty MoeFeeLine row pre-rendered by `LineRowComponent`), replaces every
 * occurrence of `__NEW_INDEX__` with a unique value, and appends the resulting
 * row to `listTarget`. Stimulus auto-mounts any controllers declared on the
 * new DOM via its mutation observer — `moe-fee-assiette` (live recompute),
 * `autofocus` (focus the libellé), and the row's accordion `<details>` all
 * connect on insertion without further wiring.
 *
 * Targets:
 *   - template: a <template data-moe-fee-line-builder-target="template"> with
 *     the empty row rendered server-side (see _edit_form.html.erb).
 *   - list: the <div data-moe-fee-line-builder-target="list" id="moe-fee-lines">
 *     the cloned row is appended to.
 *
 * Actions:
 *   - add(event): clone + append a new row.
 */
export default class extends Controller {
  static targets = ['template', 'list'];

  add(event) {
    event.preventDefault();
    if (!this.hasTemplateTarget || !this.hasListTarget) return;

    // Unique key per clone. MUST be all-digit because:
    //   1. Strong Params silently strips non-numeric outer hash keys from
    //      `moe_fee_lines_attributes` (Rails caveat — `permit` only accepts digit
    //      indices for nested-attributes hashes).
    //   2. AR's `accepts_nested_attributes_for` does NOT use the outer
    //      hash key as a record id — it looks at the inner `id:` field.
    //      So an all-digit outer key without an inner `id:` correctly
    //      maps to a new record.
    // The form fields rendered into the template carry concrete values for
    // `id` (absent) and `position` (0) so the 17-digit uniq value never
    // lands in any integer column.
    const uniq = `${Date.now()}${Math.floor(Math.random() * 1e4)}`;
    const html = this.templateTarget.innerHTML.replace(/__NEW_INDEX__/g, uniq);
    this.listTarget.insertAdjacentHTML('beforeend', html);
  }
}
